import { isEmpty } from "lodash";
import React, {
  useReducer,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { Box, Flex, FlexItem, Button, Icon } from "orcs-design-system";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import icons from "src/config/icons";
import { useTagsData } from "./hooks/useTagsData";
import TagsHierarchyViewer from "./components/TagsHierarchyViewer";
import { TagsHierarchyViewerContext } from "./context";
import { tagsReducer, initialTagsState } from "./reducers/tagsReducer";
import {
  setPagination,
  setSelectedTabType,
  setTagTypePagination,
} from "./actions/tagsActions";
import PaginationControls from "./components/PaginationControls";
import { DEFAULT_PAGINATION_LIMIT } from "./consts";

const TagsHierarchyViewerContainer = ({
  entityId,
  entityName,
  tagTypes,
  entityType = ENTITY_TYPES.GROUP,
  showContainer,
}) => {
  const [state, dispatch] = useReducer(tagsReducer, initialTagsState);
  const [editMode, setEditMode] = useState(false);
  const {
    selectedTabType,
    availableTabTypes,
    tags,
    loading,
    error,
    pagination,
    tagTypePagination,
  } = state;

  const { countsLoading, tagsQueryLoading, refetch } = useTagsData({
    entityId,
    tagTypes,
    entityType,
    pagination,
    selectedTabType,
    dispatch,
  });

  // Refetch data when first page becomes empty after tag removal
  useEffect(() => {
    if (
      tags.length === 0 &&
      pagination.totalCount > 0 &&
      pagination.offset === 0
    ) {
      refetch();
    }
  }, [tags.length, pagination.totalCount, pagination.offset, refetch]);

  const handleTabChange = useCallback(
    (newTabType) => {
      dispatch(setSelectedTabType(newTabType));

      const tabPagination = tagTypePagination[newTabType] || {
        limit: DEFAULT_PAGINATION_LIMIT,
        offset: 0,
        totalCount: 0,
      };

      dispatch(setPagination(tabPagination));
    },
    [tagTypePagination]
  );

  const handlePageChange = useCallback(
    (newOffset) => {
      dispatch(setPagination({ offset: newOffset }));

      if (selectedTabType) {
        dispatch(
          setTagTypePagination(selectedTabType, {
            ...pagination,
            offset: newOffset,
          })
        );
      }
    },
    [selectedTabType, pagination]
  );

  // Memoize context value to prevent unnecessary re-renders
  // Must be called before any early returns (Rules of Hooks)
  const contextValue = useMemo(
    () => ({
      tags,
      tagTypes: availableTabTypes,
      selectedTabType,
      loading: loading || tagsQueryLoading,
      error,
      entityId,
      entityName,
      entityType,
      editMode,
      setEditMode,
      dispatch,
      onTabChange: handleTabChange,
      onPageChange: handlePageChange,
      pagination,
    }),
    [
      tags,
      availableTabTypes,
      selectedTabType,
      loading,
      tagsQueryLoading,
      error,
      entityId,
      entityName,
      entityType,
      editMode,
      setEditMode,
      dispatch,
      handleTabChange,
      handlePageChange,
      pagination,
    ]
  );

  const totalCounts = useMemo(() => {
    return availableTabTypes?.reduce((acc, tagType) => {
      return acc + tagType.count;
    }, 0);
  }, [availableTabTypes]);

  if (countsLoading || !entityId || isEmpty(tagTypes)) {
    return null;
  }

  if (isEmpty(availableTabTypes)) {
    return null;
  }

  if ((!loading && totalCounts === 0) || error) {
    return null;
  }

  if (!showContainer) {
    return (
      <Box width="100%">
        <TagsHierarchyViewerContext.Provider value={contextValue}>
          <TagsHierarchyViewer />
        </TagsHierarchyViewerContext.Provider>
      </Box>
    );
  }

  return (
    <Box
      borderRadius={2}
      boxBorder="default"
      p="r"
      bg="white"
      width="100%"
      minWidth={0}
      display="flex"
      flexDirection="column"
    >
      <Box width="100%">
        <TagsHierarchyViewerContext.Provider value={contextValue}>
          <TagsHierarchyViewer />
        </TagsHierarchyViewerContext.Provider>
      </Box>
      <Flex justifyContent="space-between" alignItems="center" p="s">
        <FlexItem>
          {!editMode && (
            <Button
              iconLeft
              type="button"
              small
              onClick={() => setEditMode(!editMode)}
            >
              <Icon icon={icons.pen} />
              Edit
            </Button>
          )}
        </FlexItem>
        <FlexItem>
          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={tagsQueryLoading}
          />
        </FlexItem>
      </Flex>
    </Box>
  );
};

TagsHierarchyViewerContainer.propTypes = {
  tagTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  entityId: PropTypes.string.isRequired,
  entityName: PropTypes.string,
  showContainer: PropTypes.bool,
  entityType: PropTypes.string,
};

export default TagsHierarchyViewerContainer;
